import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  fetchPostBySlug,
  fetchPostsByCategoryExcludingSlugPaginated,
  fetchRecentPostsByAuthor,
} from "../../services/api"; // Import the new API function
import styles from "../SingleBlog/SingleBlog.module.css";
import Animations from "../../components/Animations/animations";
import { decodeHTML } from "../../utils";
import NotFound from "../NotFound/NotFound";

const SingleBlogPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [_slug, setSlug] = useState(slug ?? "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState("");
  const [recentPosts, setRecentPosts] = useState([]); // State to store recent posts
  const [modifiedContent, setModifiedContent] = useState("");
  const [recommendedPosts, setRecommendedPosts] = useState([]);
  const [totalRecommendedPosts, setTotalRecommendedPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  // Inside your SingleBlogPage component
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setPost(null);

      try {
        const fetchedPost = await fetchPostBySlug(_slug);
        if (!fetchedPost) {
          throw new Error("Post not found");
        }

        setPost(fetchedPost);

        // Fetch recent posts by the same author
        if (fetchedPost._embedded?.author?.[0]?.id) {
          const authorId = fetchedPost._embedded.author[0].id;
          const fetchedRecentPosts = await fetchRecentPostsByAuthor(authorId);
          setRecentPosts(fetchedRecentPosts);
        } else {
          setRecentPosts([]);
        }

        // Fetch recommended posts by category
        const categoryId = fetchedPost.categories?.[0];
        if (categoryId) {
          const { posts, totalPosts } =
            await fetchPostsByCategoryExcludingSlugPaginated(
              categoryId,
              fetchedPost.id,
              1 // Initial page
            );
          setRecommendedPosts(posts);
          setTotalRecommendedPosts(totalPosts);
          setHasMorePosts(posts.length < totalPosts);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [_slug]);

  const handleLoadMore = async () => {
    if (!post || !post.categories?.[0]) return;

    const nextPage = currentPage + 1;
    const categoryId = post.categories[0];

    try {
      const { posts, totalPosts } =
        await fetchPostsByCategoryExcludingSlugPaginated(
          categoryId,
          post.id,
          nextPage
        );

      setRecommendedPosts((prevPosts) => [...prevPosts, ...posts]);
      setCurrentPage(nextPage);
      setHasMorePosts(recommendedPosts.length + posts.length < totalPosts);
    } catch (err) {
      console.error("Error loading more posts:", err);
    }
  };

  useEffect(() => {
    if (post) {
      const headingsInContent = [];
      const parser = new DOMParser();
      const doc = parser.parseFromString(post.content.rendered, "text/html");

      // Query for all h2, h3, and h4 tags
      const headingElements = doc.querySelectorAll("h2, h3, h4");

      headingElements.forEach((heading, index) => {
        const id = `heading-${index}`;
        heading.id = id; // Assign an ID to each heading

        // Clean up the heading text
        const cleanedText = heading.innerText
          .replace(/^[\d•\s\-\.]+/, "")
          .trim();

        headingsInContent.push({ id, text: cleanedText });
      });

      // Update the headings state
      setHeadings(headingsInContent);

      // Update the content without modifying the `post` state
      const updatedContent = doc.body.innerHTML;

      // Set the updated content in a separate state or use it directly in rendering
      setModifiedContent(updatedContent);
    }
  }, [post]);

  // Handle scrolling to a specific heading
  const handleHeadingClick = (id) => {
    const headingElement = document.getElementById(id);
    if (headingElement) {
      headingElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Detect active heading when scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      {
        threshold: 0.5, // When 50% of the heading is in view
      }
    );

    // Observe all headings
    const allHeadings = document.querySelectorAll("h2, h3, h4");
    allHeadings.forEach((heading) => observer.observe(heading));

    return () => {
      allHeadings.forEach((heading) => observer.unobserve(heading));
    };
  }, []);

  if (loading) {
    return (
      <>
        <section className={styles.skeletonBanner}>
          <div className={styles.container}>
            <div className={`${styles.skeleton} ${styles.skeletontitle}`}></div>
            <div className={styles.blogAuthor}>
              <div
                className={`${styles.skeleton} ${styles.skeletonauthor}`}
              ></div>
            </div>
          </div>
        </section>
        <section className={styles.skeletonContentSec}>
          <div className={styles.container}>
            <div
              className={`${styles.skeleton} ${styles.skeletonContent}`}
            ></div>
            <div
              className={`${styles.skeleton} ${styles.skeletonSidebar}`}
            ></div>
          </div>
        </section>
      </>
    );
  }

  if (error) {
    return (
      <div>
        <NotFound/>
      </div>
    );
  }

  if (!post) {
    return (
      <div>
        <NotFound/>
      </div>
    );
  }

  const { title, content, _embedded, date } = post;
  const authorName = _embedded?.author?.[0]?.name || "Unknown Author";
  const authorBio = _embedded?.author?.[0]?.description || "Author Description"

  return (
    <>
      <section className={styles.SingleBlogBanner}>
        <div className={styles.container}>
          <Animations animationType="fadeInUp" threshold={0.2}>
            <h1>{decodeHTML(title.rendered)}</h1>
            <div className={styles.blogAuthor}>
              <div>
                <img
                  src="/assets/user.webp"
                  width="22"
                  height="22"
                  alt="Blog Author"
                />
              </div>
              <div>
                <span>{authorName}</span>
                <span> . </span>
                <span>
                  {new Date(date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </Animations>
        </div>
      </section>
      <section className={styles.blogContentSec}>
        <div className={styles.container}>
          <div
            dangerouslySetInnerHTML={{
              __html: modifiedContent || post.content.rendered,
            }}
            className={styles.blogContent}
          />

          <div className={styles.blogSidebar}>
            {/* On-Page Navigation */}
            <nav className={styles.blogOnPageNav}>
              <ul>
                <p>on this page</p>
                {headings.map((heading) => (
                  <li
                    key={heading.id}
                    className={
                      activeHeading === heading.id ? styles.activeheading : ""
                    }
                    onClick={() => handleHeadingClick(heading.id)}
                  >
                    {heading.text}
                  </li>
                ))}
              </ul>
            </nav>

            <div className={styles.blogAuthourAbout}>
              <div>
                <img
                  src="/assets/user.webp"
                  width="22"
                  height="22"
                  alt="Blog Author"
                />
              </div>
              <div>
                <h6 className={styles.authorName}>{authorName}</h6>
                <p className={styles.authorDescription}>
{authorBio}                </p>
              </div>
            </div>
            <hr />

            <div className={styles.recentBlogs}>
              <h6>Recent Articles by {authorName}</h6>
              <div className={styles.recentBlogsList}>
                {recentPosts.length > 0 ? (
                  recentPosts.map((recentPost) => {
                    // Get the featured image from _embedded
                    const featuredImage =
                      recentPost._embedded?.["wp:featuredmedia"]?.[0]
                        ?.source_url;
                    return (
                      <div
                        key={recentPost.id}
                        className={styles.recentBlogCard}
                      >
                        {featuredImage && (
                          <div className={styles.recentBlogImage}>
                            <Link
                              to={`/blog/${recentPost.slug}`}
                              rel="noopener noreferrer"
                              onClick={() => setSlug(recentPost.slug)}
                            >
                              <img
                                src={featuredImage}
                                alt={recentPost.title.rendered}
                                width="100%"
                                height="auto"
                              />
                            </Link>
                          </div>
                        )}
                        <h6>
                          <Link
                            to={`/blog/${recentPost.slug}`}
                            rel="noopener noreferrer"
                            onClick={() => setSlug(recentPost.slug)}
                          >
                            {decodeHTML(recentPost.title.rendered)}
                          </Link>
                        </h6>
                      </div>
                    );
                  })
                ) : (
                  <p>No recent posts found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {totalRecommendedPosts && (
        <section className={styles.recomendedBlogsSec}>
          <div className={styles.container}>
            <div className={styles.recomendedBlogsSecHeader}>
              <div>
                {" "}
                <h2>Recommended Articles</h2>
                <p className={styles.subText}>
                  Explore Thousands of Expertly Curated Articles with Our
                  Powerful Algorithm
                </p>
              </div>
              <div>
                {hasMorePosts && (
                  <button
                    className={styles.loadMoreButtonDesktop}
                    onClick={handleLoadMore}
                  >
                    Load More
                  </button>
                )}
              </div>
            </div>

            <div className={styles.recomendedBlogsCardWrapper}>
              {recommendedPosts.length > 0 ? (
                recommendedPosts.map((recommendedPost) => {
                  const featuredImage =
                    recommendedPost._embedded?.["wp:featuredmedia"]?.[0]
                      ?.source_url;
                  return (
                    <Link
                   to={`/blog/${recommendedPost.slug}`}
                   onClick={() => setSlug(recommendedPost.slug)}
                   rel="noopener noreferrer"
                 >
                    <div
                      key={recommendedPost.id}
                      className={styles.recomendedBlogsCard}
                    >
                      {/* Featured Image */}
                      {featuredImage && (
                        <div className={styles.recomendedBlogs__cardImg}>
                          <img
                            src={featuredImage}
                            alt={recommendedPost.title.rendered}
                            width="100%"
                            height="100%"
                            loading="lazy"
                          />
                        </div>
                      )}

                      {/* Blog Meta */}
                      <div className={styles.blogMeta}>
                        <h6>
                         
                            {decodeHTML(recommendedPost.title.rendered)}
                        
                        </h6>
                        <div className={styles.blogAuthor}>
                          <div>
                            <img
                              src="/assets/user.webp"
                              width="22"
                              height="22"
                              alt="Blog Author"
                            />
                          </div>
                          <div>
                            <span>
                              {recommendedPost._embedded?.author?.[0]?.name ||
                                "Author"}
                            </span>
                            <span> . </span>
                            <span>
                              {new Date(
                                recommendedPost.date
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    </Link> 
                  );
                })
              ): (
                <p>No recommended articles found</p>
              )}
            </div>
            <div>
              {hasMorePosts && (
                <button
                  className={styles.loadMoreButtonMobile}
                  onClick={handleLoadMore}
                >
                  Load More
                </button>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default SingleBlogPage;
